import { queryOptions } from '@tanstack/react-query';

import { parseSchema } from '@repo/utils/parse-schema';

import { http, NotFoundError } from '~/services/http.ts';

import type { ComponentValueUpdate } from './types.ts';
import {
  cargoCompositionStatusSchema,
  declarationFormSchema,
  declarationSchema,
  validationResponseSchema,
} from './types.ts';

const cargoApiBase = `${import.meta.env.VITE_API_BASE}/cargo`;

function projectCompositionBase(projectId: string) {
  return `${cargoApiBase}/projects/${projectId}/composition`;
}

export async function getCargosWithCompositionStatus(projectId: string, includeCompleted = false) {
  const params = new URLSearchParams();
  if (includeCompleted) {
    params.set('includeCompleted', 'true');
  }
  const url = `${projectCompositionBase(projectId)}/cargos${params.toString() ? `?${params.toString()}` : ''}`;
  return parseSchema(cargoCompositionStatusSchema.array(), await http(url));
}

export function queryCargosWithCompositionStatus(projectId: string, includeCompleted = false) {
  return queryOptions({
    queryFn: () => getCargosWithCompositionStatus(projectId, includeCompleted),
    queryKey: [projectId, 'declarations', { includeCompleted }],
  });
}

export async function getDeclaration(projectId: string, declarationId: string) {
  return parseSchema(declarationSchema, await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}`));
}

export function queryDeclaration(projectId: string, declarationId: string) {
  return queryOptions({
    queryFn: () => getDeclaration(projectId, declarationId),
    queryKey: ['declaration', projectId, declarationId],
  });
}

export async function getDeclarationForm(projectId: string, declarationId: string) {
  return parseSchema(declarationFormSchema, await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}/form`));
}

export function queryDeclarationForm(projectId: string, declarationId: string) {
  return queryOptions({
    queryFn: () => getDeclarationForm(projectId, declarationId),
    queryKey: ['declaration', projectId, declarationId, 'form'],
  });
}

/**
 * Fetch the form of the most recent declared declaration for the project — used to seed a
 * fresh draft ("Copy from previous"). Matches on isPreliminary so final drafts seed from
 * final declarations and preliminary drafts seed from preliminary. Returns null if nothing
 * suitable exists.
 */
export async function getPreviousDeclarationForm(
  projectId: string,
  { isPreliminary, excludeDeclarationId }: { isPreliminary: boolean; excludeDeclarationId: string },
) {
  const params = new URLSearchParams({
    status: 'Declared',
    isPreliminary: String(isPreliminary),
    sort: 'declaredAt:desc',
    pageSize: '5',
    page: '1',
  });
  const response = await http<{ items?: { id: string; declaredAt?: string | null }[] }>(
    `${projectCompositionBase(projectId)}/declarations?${params.toString()}`,
  );
  const items = response?.items ?? [];
  const match = items.find(item => item.id !== excludeDeclarationId);
  if (!match)
    return null;

  return getDeclarationForm(projectId, match.id);
}

export function queryPreviousDeclarationForm(
  projectId: string,
  opts: { isPreliminary: boolean; excludeDeclarationId: string },
) {
  return queryOptions({
    queryFn: () => getPreviousDeclarationForm(projectId, opts),
    queryKey: [
      'declaration',
      projectId,
      'previous-declared-form',
      { isPreliminary: opts.isPreliminary, excludeDeclarationId: opts.excludeDeclarationId },
    ],
    // Avoid hammering the browse endpoint on every form render; the "most recent declared"
    // changes infrequently and we refetch on mount anyway.
    staleTime: 60 * 1000,
  });
}

export async function getValidation(projectId: string, declarationId: string) {
  return parseSchema(validationResponseSchema, await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}/validation`));
}

export function queryValidation(projectId: string, declarationId: string) {
  return queryOptions({
    queryFn: () => getValidation(projectId, declarationId),
    queryKey: ['declaration', projectId, declarationId, 'validation'],
  });
}

export async function createDeclaration(
  projectId: string,
  cargoId: string,
  batchId: string | null = null,
  isPreliminary = false,
) {
  return parseSchema(declarationSchema, await http(`${projectCompositionBase(projectId)}/cargos/${cargoId}/declarations`, {
    method: 'POST',
    body: { batchId, isPreliminary },
  }));
}

export async function updateDeclaration(
  projectId: string,
  declarationId: string,
  componentValues: ComponentValueUpdate[],
  generalNote?: string | null,
) {
  return parseSchema(declarationSchema, await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}`, {
    method: 'PUT',
    body: { componentValues, generalNote: generalNote ?? null },
  }));
}

export async function updatePreliminaryDeclaration(
  projectId: string,
  declarationId: string,
  componentValues: ComponentValueUpdate[],
  generalNote?: string | null,
) {
  return parseSchema(declarationSchema, await http(`${projectCompositionBase(projectId)}/preliminary/${declarationId}`, {
    method: 'PUT',
    body: { componentValues, generalNote: generalNote ?? null },
  }));
}

export async function submitDeclaration(projectId: string, declarationId: string) {
  return parseSchema(declarationSchema, await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}/declare`, { method: 'POST' }));
}

export async function submitPreliminaryDeclaration(projectId: string, declarationId: string) {
  return parseSchema(declarationSchema, await http(`${projectCompositionBase(projectId)}/preliminary/${declarationId}/declare`, { method: 'POST' }));
}

export async function getDeclarationPdfUrl(projectId: string, declarationId: string) {
  const response = await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}/pdf`) as { url: string };
  return response.url;
}

export async function getDeclarationPdfPreviewUrl(projectId: string, declarationId: string) {
  const response = await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}/pdf/preview`) as { url: string };
  return response.url;
}

export async function deleteDeclaration(projectId: string, declarationId: string) {
  await http(`${projectCompositionBase(projectId)}/declarations/${declarationId}`, { method: 'DELETE' });
}

export async function getLogoUrl(projectId: string) {
  // 404 is the normal response when a project has no logo set — treat it as "no logo"
  // rather than letting the NotFoundError bubble up and kill the route on load.
  try {
    const result = await http<{ url: string }>(`${projectCompositionBase(projectId)}/logo`);
    return result?.url ?? null;
  }
  catch (error) {
    if (error instanceof NotFoundError)
      return null;
    throw error;
  }
}

export function queryLogoUrl(projectId: string) {
  return queryOptions({
    queryFn: () => getLogoUrl(projectId),
    queryKey: ['composition', 'projects', projectId, 'logo'],
    staleTime: 5 * 60 * 1000,
  });
}

export async function uploadLogo(projectId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  await http(`${projectCompositionBase(projectId)}/logo`, { method: 'POST', body: formData });
}

export async function deleteLogo(projectId: string) {
  await http(`${projectCompositionBase(projectId)}/logo`, { method: 'DELETE' });
}
